/**
 * Bridge ops-surface API (SKBRIDGE-CC-003 CH-U1). Three Softknack-internal
 * endpoints backing the Ops screen's three panels:
 *   GET /observability/ops/connections — per-org connector grants + cred health
 *   GET /observability/ops/executions  — the ADR-001 egress audit (+ CSV)
 *   GET /observability/ops/failures    — per-connector failure rollup
 */
import { apiFetch } from './client';
import { API_BASE, API_PREFIX, DEV_TENANT_ID } from './config';

export interface OpsConnection {
  connection_id: string;
  org: string;
  connector: string;
  connector_name: string;
  status: string;
  auth_method: string;
  secret_ref: string;
  connected_at: string;
  updated_at: string;
  webhook_secret_rotated_at: string | null;
  oauth_health: 'ok' | 'refresh_failing';
}

export interface OpsExecution {
  execution_id: string;
  executed_at: string;
  platform_org_id: string | null;
  pre_audit: boolean;
  connector: string;
  operation: string | null;
  canonical_tool: string | null;
  status: string;
  error_category: string | null;
  latency_ms: number | null;
}

export interface OpsExecutionPage {
  executions: OpsExecution[];
  total: number;
  pre_audit_in_range: number;
}

export interface FailureCard {
  connector: string;
  connector_name: string;
  total: number;
  failed: number;
  fail_rate: number;
  categories: Record<string, number>;
  buckets: Array<Record<string, number | string>>;
  last_error: { at: string; category: string | null; operation: string | null } | null;
}

export type OpsFilters = Record<string, string | number | undefined>;

function qs(filters: OpsFilters): string {
  const parts = Object.entries(filters)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export function fetchOpsConnections(filters: OpsFilters = {}): Promise<{ connections: OpsConnection[] }> {
  return apiFetch(`/observability/ops/connections${qs(filters)}`, { internal: true });
}

export function fetchOpsExecutions(filters: OpsFilters = {}): Promise<OpsExecutionPage> {
  return apiFetch(`/observability/ops/executions${qs(filters)}`, { internal: true });
}

export function fetchOpsFailures(
  window: '24h' | '7d' | '30d',
  org?: string,
): Promise<{ window: string; connectors: FailureCard[] }> {
  return apiFetch(`/observability/ops/failures${qs({ window, org })}`, { internal: true });
}

/**
 * CSV export. The viewer's sandbox blocks script-started downloads, so we fetch
 * the bytes with the same internal auth headers the API requires and hand the
 * browser a blob URL — a plain <a href> would arrive unauthenticated.
 */
export async function exportExecutionsCsv(filters: OpsFilters = {}): Promise<void> {
  const url = `${API_BASE}${API_PREFIX}/observability/ops/executions${qs({ ...filters, format: 'csv' })}`;
  const res = await fetch(url, { headers: { 'x-tenant-id': DEV_TENANT_ID, 'x-sk-internal': 'true' } });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'bridge-egress.csv';
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}
